import React from 'react'
import { Field } from 'formik'
import { darken, lighten } from '@material-ui/core/styles/colorManipulator'
import styled, { css } from 'styled-components'
import {
  getColorErrorSelector,
  getColorPrimarySelector
} from '@purecarslabs/signal-theme/selectors'
import isDarkColor from '@purecarslabs/signal-utils/isDarkColor'
import { FORM_MAX_WIDTH_PX } from '@app-src/constants'

const MODAL_FORM_WIDTH_PX = 400

// gets the correct padding for a field, depending on whether the field is embdded (or in a modal), and
// if it's in a modal, what the screen height is
function getFieldPadding({ $isEmbedded }: { $isEmbedded: boolean }) {
  return `${
    $isEmbedded
      ? '10px 12px;'
      : `8px 12px;
  @media only screen and (max-height: 784px) {
    padding: 6px 12px;
  }
  @media only screen and (max-height: 700px) {
    padding: 4px 12px;
  }`
  }`
}

export const FormContents = styled.div<{
  $backgroundColor: string | undefined
  $everSubmitted: boolean
  $isEmbedded: boolean
  $textColor: string | undefined
}>`
  width: 100%;
  ${({ $isEmbedded }) =>
    $isEmbedded
      ? 'width: 100%;'
      : `@media only screen and (min-width: 480px) { width: ${MODAL_FORM_WIDTH_PX}px; }`};
  max-width: ${FORM_MAX_WIDTH_PX}px;
  font-size: 18px;

  @media only screen and (max-width: 480px) {
    width: 100%;
  }

  & input:not([type="checkbox"]) {
    transition: scale 0.1s ease-out;
    border-radius: 15px;
    padding: ${getFieldPadding}
    box-shadow: 3px 3px 4px #ccc;
    margin-top: 16px;

    /* attempt to prevent autofill styling from changing our styling */
    :-webkit-autofill {
      transition: scale 0.1s ease-out;
    }

    :-webkit-autofill,
    :-webkit-autofill:hover,
    :-webkit-autofill:focus,
    :-webkit-autofill:active {
       filter: none;
       transition:
         /* hack to prevent requests from the browser to force the color of autofill from appearing */
         background-color 99999s ease-in-out 0s, 
         color 99999s ease-in-out 0s;
        
    }

    ::-internal-input-suggested {
       transition: 
         /* hack to prevent requests from the browser to force the color of autofill from appearing */
         font-size 99999s ease-in-out 0s, 
         color 99999s ease-in-out 0s;
    }

    :-webkit-autofill:hover,
    :-webkit-autofill:focus,
    :hover, 
    :focus {
      scale: 104%;
    }
  }

  /* Terms & conditions checkbox - it requires a custom error color so needs to be a custom implementation */

  & input[type="checkbox"] {
    appearance: none;
    -webkit-appearance: none;
    display: flex;
    align-content: center;
    justify-content: center;
    padding: 3px;
    border: 1px solid grey;
    border-radius: 20px;
    height: 19px;
    cursor: pointer;
    background: white;
  }

  /* render the checkbox here */
  & input[type="checkbox"]::before {
    content: "";
    width: 10px;
    height: 10px;
    margin-left: 1px;
    margin-top: 1px;
    clip-path: polygon(10% 40%, 40% 60%, 90% 0%, 100% 10%, 40% 100%, 0% 60%, 0% 50%);
    transform: scale(0);
    background: white;
  }

  & input[type="checkbox"]:checked {
    background: blue;
  }

  & input[type="checkbox"]:checked::before {
    transform: scale(1);
  }

  & input[type="checkbox"]:hover {
    border: 2px solid grey;
    padding 2px;
  }

  & select {
    transition: scale 0.1s ease-out;
    border-radius: 15px;
    padding: ${getFieldPadding}
    box-shadow: 3px 3px 4px #ccc;
    margin-top: 16px;

    /* custom select control carat */
    background: url("data:image/svg+xml,<svg height='10px' width='10px' viewBox='0 0 16 16' fill='${({
      $textColor
    }) =>
      $textColor
        ? encodeURIComponent($textColor)
        : '%23000000'}' xmlns='http://www.w3.org/2000/svg'><path d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/></svg>") no-repeat !important;
    background-position: calc(100% - 12px) center !important;
    -moz-appearance:none !important;
    -webkit-appearance: none !important; 
    appearance: none !important;
    padding-right: 32px !important;

    :hover {
      scale: 104%;
    }
  }

  & textarea {
    resize: vertical;
    transition: scale 0.1s ease-out;
    padding: ${getFieldPadding}
    margin-top: 16px;
    border-radius: 10px;
    box-shadow: 2px 2px 2px 2px #ddd;
    height: 80px;

    @media only screen and (max-height: 784px) {
      height: 60px;
    }

    :hover {
      scale: 104%;
    }
  }

  .form-error {
    color: ${props => {
      const adjustFunc =
        props.$backgroundColor && isDarkColor(props.$backgroundColor)
          ? lighten
          : darken
      return adjustFunc(
        getColorErrorSelector()(props),
        !props.$backgroundColor ||
          !isDarkColor(darken(props.$backgroundColor, 0.5)) ||
          isDarkColor(lighten(props.$backgroundColor, 0.5))
          ? 0
          : 0.4
      )
    }};
  }

  .accept-terms-error {
    margin: 8px 0 0 30px;
  }
`

export const FormError = styled.p`
  color: red;
  font-size: 14px;
  margin: 0 0 0 12px;
`

export const FormOuter = styled.div<{ $isEmbedded: boolean }>`
  font-size: 32px;
  display: flex;
  flex-direction: column;
  align-items: ${({ $isEmbedded }) => ($isEmbedded ? 'flex-start' : 'center')};
  justify-content: space-between;
`

export const FormSubmitError = styled.p`
  color: red;
  margin-top: 14px;
  margin-bottom: 0px;
  font-size: 16px;
  line-height: 18px;
  text-align: center;

  @media only screen and (max-height: 784px) {
    margin-top: 8px;
  }
`

export const FormTitle = styled.h4<{ $titleColor?: string }>`
  color: ${({ $titleColor }) => $titleColor || getColorPrimarySelector()};
  text-align: center;
  font-family: 'PC LeadForms Custom Font Bold',
    'PC LeadForms Custom Font Normal', 'Source Sans', sans-serif;
  font-size: 32px;
  margin: 0;
  font-style: normal;

  @media only screen and (max-width: 480px) {
    font-size: 24px;
  }
  @media only screen and (max-height: 784px) {
    font-size: 24px;
  }
`

export const StyledField = styled(
  ({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    $alsoRequired,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    $anyHaveError,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    $backgroundColor,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    $hasError,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    $placeholderColor,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    $textColor,
    ...rest
  }) => <Field {...rest} />
)`
  -webkit-border-radius: 5px;
  -moz-border-radius: 5px;
  border-radius: 5px;
  display: block;
  font-size: 18px;
  box-sizing: border-box;
  border-color: ${({ $hasError }) => ($hasError ? 'red' : '#999')};
  border-style: solid;
  border-width: ${({ $hasError }) => ($hasError ? '2' : '1')}px;
  margin-bottom: 7px;
  margin-top: 1rem;
  padding: 5px;
  width: 100%;
  color: ${({ $textColor }) => $textColor || 'black'};

  &::placeholder {
    color: ${({ $placeholderColor }) => $placeholderColor || '#aaa'};
  }

  :not(select) {
    &:focus {
      background: ${({ $backgroundColor }) =>
        $backgroundColor || 'white'} !important;
    }

    &:not(:focus) {
      background: ${props => props.$backgroundColor || 'white'} !important;
    }
  }

  :not(:not(select)) {
    background-color: ${({ $backgroundColor }) =>
      $backgroundColor || 'white'} !important;

    &:not(:focus) {
      background-color: ${props =>
        props.$backgroundColor || 'white'} !important;
    }
  }
`

/**
 * Terms and conditions disclaimer
 */

const termsConditionsCommon = css`
  padding: 0;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
`

export const TermsConditionsHeader = styled.p`
  ${termsConditionsCommon}
  font-size: 12.5px;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  & input {
    margin-top: 0;
    margin-right: 6px;
  }
`

export const TermsConditionsBody = styled.p<{ $mustAcceptTerms: boolean }>`
  ${termsConditionsCommon}
  font-size: 10.5px;
  margin 5px 0 0 ${({ $mustAcceptTerms }) => ($mustAcceptTerms ? '30px' : '0')};
  overflow-y: scroll;
  height: 71px;
`

export const TermsConditionsSpan = styled.span`
  width: 100%;
`

export const SkeletonWrapper = styled.div<{ $isEmbedded: boolean }>`
  ${({ $isEmbedded }) =>
    !$isEmbedded ? `width: ${MODAL_FORM_WIDTH_PX}px;` : ''}
  max-width: ${FORM_MAX_WIDTH_PX}px;
  height: 320px;
`
